import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { getJiraIssueApi } from './chatApi'
import { ChatLinkIcon } from './ChatLinkIcon'
import ChatExpandTooltip from './ChatExpandTooltip'
import { resolveChatLink } from './chatLinks'
import { jiraBrowseUrl } from './jiraLinks'

const titleCache = new Map<string, string>()
const inflight = new Map<string, Promise<string>>()

async function loadSummary(issueKey: string): Promise<string> {
  const cached = titleCache.get(issueKey)
  if (cached) return cached
  const existing = inflight.get(issueKey)
  if (existing) return existing
  const promise = getJiraIssueApi(issueKey)
    .then((issue) => {
      const summary = issue.summary?.trim() || issueKey
      titleCache.set(issueKey, summary)
      return summary
    })
    .catch(() => issueKey)
    .finally(() => {
      inflight.delete(issueKey)
    })
  inflight.set(issueKey, promise)
  return promise
}

type JiraIssueLinkProps = {
  issueKey: string
  href?: string
  className?: string
}

const JiraIssueLink: React.FC<JiraIssueLinkProps> = ({ issueKey, href, className }) => {
  const key = issueKey.toUpperCase()
  const [summary, setSummary] = useState(() => titleCache.get(key) ?? null)

  useEffect(() => {
    let cancelled = false
    void loadSummary(key).then((next) => {
      if (!cancelled) setSummary(next === key ? null : next)
    })
    return () => {
      cancelled = true
    }
  }, [key])

  const resolved = resolveChatLink(href)
  const url = resolved?.kind === 'external' ? resolved.href : jiraBrowseUrl(key)
  const expansion = summary ? `${key}: ${summary}` : null

  return (
    <ChatExpandTooltip content={expansion}>
      <a
        href={url}
        target="_blank"
        rel="noreferrer noopener"
        aria-label={expansion ?? key}
        className={
          className ??
          'inline-flex max-w-full items-start font-medium text-indigo-600 no-underline hover:text-indigo-500 dark:text-indigo-300'
        }
      >
        <ChatLinkIcon kind="jira" href={url} />
        <span className="min-w-0 underline underline-offset-2">
          <span className="font-mono text-[12px]">{key}</span>
        </span>
        <span className="ml-1 inline-flex h-[1lh] shrink-0 items-center" aria-hidden>
          <ArrowTopRightOnSquareIcon className="inline-block h-3.5 w-3.5 bg-transparent no-underline" />
        </span>
      </a>
    </ChatExpandTooltip>
  )
}

export default JiraIssueLink
